import { type TFunction } from 'i18next';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { type ConfigObject, DebugLevel } from '../_configs/types';
import { createLog } from '../_tools/axios/createLog';
import { DBLogLevel, DBLogSource } from '../_tools/types/log';
import ErrorCard from '../components/error-card';
import { SettingsContext } from '../context/settings';
import { type ConfigErrorType } from '../errors/ConfigError';
import { type CustomerErrorType } from '../errors/CustomerError';
import { type NetworkErrorType } from '../errors/NetworkError';
import { type RuntimeErrorType } from '../errors/RuntimeError';
import { type TotalausfallErrorType } from '../errors/TotalausfallError';
import { type UnknownErrorType } from '../errors/UnknownError';
import { type Translations } from '../locales/translations';
import { type Modules } from '../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../utils/tools/checkUndefinedNullOrEmpty';
import enhancedNavigate from '../utils/tools/enhancedNavigate';
import { LOCALSERVER } from '../vars';

export type CustomErrorType = {
    code?: string;
    message?: string;
    stack?: string;
};

type IState = {
    error?: CustomErrorType;
};

type IProps = {
    children: any;
    module: Modules;
    t: TFunction;
};

type CustomError = ConfigErrorType | CustomerErrorType | NetworkErrorType | RuntimeErrorType | UnknownErrorType | TotalausfallErrorType;

class ErrorHandler extends Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    shouldComponentUpdate() {
        return true;
    }

    componentDidCatch(error: CustomError) {
        const newError = Object.assign(
            {},
            {
                code: error?.code,
                message: error?.message,
                name: error?.name,
                stack: error?.stack,
            }
        );
        if (checkUndefinedNullOrEmpty(newError.code)) {
            if (!checkUndefinedNullOrEmpty(newError.stack)) {
                newError.code = 'runtimeError';
            } else {
                newError.code = 'unknown';
            }
        }
        this?.setState({ error: newError });
    }

    handleErrorAction(action: string) {
        if (action === 'reloadPage') location.reload();
        // else if (action === 'exitError') this?.setState({ error: null });
        else if (action === 'goHome') {
            this?.setState({ error: null });
            enhancedNavigate((this?.context as ConfigObject)?.general?.searchPath);
        }
    }

    render() {
        const { children, module, t } = this.props;
        const { error } = this.state;
        const code = error?.code as keyof Translations['errorHandler']['sIntErrors'];
        const settings = this?.context as ConfigObject;

        if (!checkUndefinedNullOrEmpty(error)) {
            const action = error?.code?.startsWith('4x') ? undefined : error?.code?.startsWith('3x') ? 'goHome' : 'reloadPage';

            if (error?.code?.startsWith('4x005') || error?.code?.startsWith('4x006')) {
                return (
                    <ErrorCard
                        heading={t('common:errorHandler.moduleDisabled.heading')}
                        description={t('common:errorHandler.moduleDisabled.subtitle')}
                        level="grey"
                        module={module}
                        settings={settings}
                    />
                );
            } else if (error?.code === 'totalausfall') {
                return (
                    <ErrorCard
                        heading={t(`common:errorHandler.sIntErrors.${code}.title`)}
                        description={t(`common:errorHandler.sIntErrors.${code}.details`)}
                        level="info"
                        module={module}
                        settings={settings}
                    />
                );
            } else {
                createLog({
                    idProject: settings?.general?.idFirma,
                    level: DBLogLevel.Error,
                    message: error?.code + ' at ' + location.href,
                    raw: error,
                    origin: DBLogSource.HPT,
                    title: error?.code,
                }, settings?.general?.debug);
                return (
                    <ErrorCard
                        action={action}
                        actionTooltip={t(`common:errorHandler.error.actions.${action}.tooltip`)}
                        collapse={
                            ['unknown', 'networkError', 'runtimeError'].includes(error?.code)
                                ? {
                                      code: checkUndefinedNullOrEmpty(error?.code) ? 'unknown' : error?.code,
                                      message: checkUndefinedNullOrEmpty(error?.code) ? error?.message : t(`common:errorHandler.sIntErrors.${code}.title`),
                                      stack:
                                          (LOCALSERVER || settings?.general?.debug >= DebugLevel.ERRORS) && (checkUndefinedNullOrEmpty(error?.code) || error?.code === 'runtimeError')
                                              ? error?.stack
                                              : t(`common:errorHandler.sIntErrors.${code}.details`),
                                  }
                                : undefined
                        }
                        description={t(['unknown', 'networkError', 'runtimeError'].includes(error?.code) ? 'common:errorHandler.error.subtitle' : `common:errorHandler.sIntErrors.${code}.details`)}
                        heading={t(['unknown', 'networkError', 'runtimeError'].includes(error?.code) ? 'common:errorHandler.error.heading' : `common:errorHandler.sIntErrors.${code}.title`)}
                        level="error"
                        module={module}
                        settings={settings}
                        setErrorState={(err) => {
                            this?.setState({ error: err });
                        }}
                    />
                );
            }
        } else {
            return children;
        }
    }
}

ErrorHandler.contextType = SettingsContext;

export default withTranslation()(React.memo(ErrorHandler));
